import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../../supabase/supabase.config";
import create from "zustand";
import { persist } from "zustand/middleware";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import { initPushNotifications } from "../../Services/pushNotifications";
import { useMixpanel } from "../MixpanelContext/MixpanelProvider";
import { useLeagueStore } from "../../zustandStore/leagueStore";
import { usePaywallStore } from "../../zustandStore/paywallStore";
import { useAdvancedStatsPromoStore } from "../../zustandStore/advancedStatsPromoStore";
import { useBookaCallStore } from "../../zustandStore/bookaCallStore";

interface User {
  email?: string;
  id?: string;
  [key: string]: any;
}

interface AuthContextType {
  signInWithGoogle: () => Promise<boolean | undefined>;
  signInWithApple: () => Promise<void>;
  signout: () => Promise<void>;
  sendSMS: (phoneNumber: string) => Promise<boolean>;
  verifyOTPCode: (phone: string, token: string) => Promise<boolean | undefined>;
  resendOtpCode: (phone: string) => Promise<boolean | undefined>;
  uploadAvatar: (
    file: any,
    userId: string,
  ) => Promise<string | undefined | null>;
  useAuthStore: any;
  user: User | null;
  setAvatarUrl: (url: string | null) => void;
  uploading: boolean;
  avatarUrl: string | null;
  resendOtpCodeLoading: boolean;
  getUserProfile: (userId: string) => Promise<any | undefined | null>;
  getUserData: () => Promise<any | null | undefined>;
  getProfileForDetails: (userId: string) => Promise<any>;
  getProfileAllowNotifications: (userId: string) => Promise<any>;
  signupProfile: (
    userId: string,
    firstName: string,
    lastName: string,
    email: string,
    referralCode: string,
  ) => Promise<any>;
  setProfileAllowNotifications: (
    userId: string,
    allow_notifications: boolean,
  ) => Promise<void>;
  getSessionwitAccessToken: (accessToken: string) => Promise<any>;
  saveAppleSubscription: (userId: string, product: any) => Promise<void>;
  deactivateAccount: (userId: string) => Promise<void>;
  assignPaywallGroup: (userId: string) => Promise<any>;
}

interface AuthContextProp {
  children: React.ReactNode;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const AuthContextProvider: React.FC<AuthContextProp> = ({
  children,
}) => {
  const [user, setUser] = useState<any>(null);
  // const [profile, setProfile] = useState<any>(null);

  const [uploading, setUploading] = useState(false);
  const [resendOtpCodeLoading, setResendOtpCodeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const Mixpanel = useMixpanel();
  const [distinctId, setDistinctId] = useState<string | null>(null);
  const { clearSelectedLeague, clearLeagueType, clearScoringFormat } = useLeagueStore();
  const { clearPaywallState } = usePaywallStore();
  const { clearPromoState } = useAdvancedStatsPromoStore();
  const { resetCount } = useBookaCallStore();
  const useAuthStore = create<any>(
    persist(
      (set, get) => ({
        user: null,
        setUser: (user: any) => set({ user }),
        clearUser: () => set({ user: null }),
        profile: null,
        setProfile: (profile: any) => set({ profile }),
        clearProfile: () => set({ profile: null }),
        setAppleProfile: (appleProfile: any) => set({ appleProfile }),
        clearAppleProfile: () => set({ appleProfile: null }),
        distinctId: null,
        setDistinctId: (id: string) => set({ distinctId: id }),
      }),
      {
        name: "auth-token",
        getStorage: () => localStorage,
      },
    ),
  );

  // Generate or retrieve anonymous distinct ID on app load
  useEffect(() => {
    const generateDistinctId = () => {
      // Check if we have a user session first
      const session = useAuthStore.getState().user;
      if (session?.user?.id) {
        // If user is logged in, always use their real ID
        const userId = session.user.id;
        setDistinctId(userId);
        Mixpanel.identifyUser(userId);
        return userId;
      }

      // If no logged in user, check for stored anonymous ID
      const storedDistinctId = useAuthStore.getState().distinctId;
      if (storedDistinctId) {
        setDistinctId(storedDistinctId);
        Mixpanel.identifyUser(storedDistinctId);
        return storedDistinctId;
      }

      // Generate a new anonymous ID if nothing exists
      const newDistinctId = `anon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      useAuthStore.getState().setDistinctId(newDistinctId);
      setDistinctId(newDistinctId);
      Mixpanel.identifyUser(newDistinctId);
      return newDistinctId;
    };

    generateDistinctId();
  }, [Mixpanel]);

  useEffect(() => {
    const checkSession = async () => {
      console.log("checking Supabase session on load");
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error("error retrieving session:", error);
      } else {
        console.log("supabase session on load:", session);
        if (session) {
          setUser(session.user.user_metadata);
          await useAuthStore.getState().setUser(session);
          getUserProfile(session?.user?.id);

          // Associate pre-login events with the logged-in user
          const userId = session.user.id;
          const currentDistinctId =
            distinctId || useAuthStore.getState().distinctId;
          const isSuperUser =
            useAuthStore.getState().profile?.is_super_user || false;

          if (currentDistinctId && currentDistinctId !== userId) {
            // This handles the case for each device independently
            console.log(
              `Aliasing anonymous ID ${currentDistinctId} with user ID ${userId}`,
            );
            Mixpanel.alias(userId, currentDistinctId);

            // Then identify the user and set properties
            Mixpanel.identifyUser(userId);
            Mixpanel.setUserProperties({
              email: session.user.email,
              phone: session.user.phone,
              provider: session.user.app_metadata?.provider,
              superuser: isSuperUser,
            });
          } else {
            // User is already identified, just make sure properties are set
            Mixpanel.identifyUser(userId);
            Mixpanel.setUserProperties({
              email: session.user.email,
              phone: session.user.phone,
              provider: session.user.app_metadata?.provider,
              superuser: isSuperUser,
            });
          }

          // Update the stored distinct ID to be the user ID
          useAuthStore.getState().setDistinctId(userId);
          setDistinctId(userId);
        } else {
          console.log("no session found on load.");
        }
      }
    };

    checkSession();

    // Initialize SocialLogin for Google authentication
    const initializeSocialLogin = async () => {
      try {
        await SocialLogin.initialize({
          google: {
            webClientId: "62104652584-b2vvk4ibsj13bsoatna0enkgaj5uct1e.apps.googleusercontent.com",
            iOSClientId: "62104652584-bdsgtfnr9qdpiv2roop8hqsopp52aush.apps.googleusercontent.com",
            iOSServerClientId: "62104652584-b2vvk4ibsj13bsoatna0enkgaj5uct1e.apps.googleusercontent.com",
          },
        });
        console.log("SocialLogin initialized successfully");
      } catch (error) {
        console.error("Error initializing SocialLogin:", error);
      }
    };

    initializeSocialLogin();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Supabase auth event: ", event, session);
        if (event === "SIGNED_OUT") {
          useAuthStore.getState().clearUser();
          setUser(null);

          // Track the logout event before creating a new anonymous ID
          Mixpanel.trackEvent("User Logged Out", {
            timestamp: new Date().toISOString(),
          });

          // After tracking logout, create a new anonymous ID for this device
          const newAnonId = `anon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          useAuthStore.getState().setDistinctId(newAnonId);
          setDistinctId(newAnonId);

          // Reset Mixpanel for anonymous tracking
          Mixpanel.reset();
          Mixpanel.identifyUser(newAnonId);
        } else if (event === "SIGNED_IN" && session) {
          setUser(session.user.user_metadata);
          console.log("user data is here", session.user.user_metadata);
          await useAuthStore.getState().setUser(session);
          const userId = session.user.id;

          if (userId) {
            const currentDistinctId =
              distinctId || useAuthStore.getState().distinctId;

            const isSuperUser = useAuthStore.getState().profile?.is_super_user;
            if (currentDistinctId && currentDistinctId !== userId) {
              // This is device-specific - alias anonymous events on this device to the user ID
              console.log(
                `Aliasing anonymous ID ${currentDistinctId} with user ID ${userId}`,
              );
              Mixpanel.alias(userId, currentDistinctId);

              // Then identify the user with their real ID
              Mixpanel.identifyUser(userId);
              Mixpanel.setUserProperties({
                email: session.user.email,
                phone: session.user.phone,
                provider: session.user.app_metadata?.provider,
                superuser: isSuperUser,
              });
            } else {
              // User is already identified correctly
              Mixpanel.identifyUser(userId);
              Mixpanel.setUserProperties({
                email: session.user.email,
                phone: session.user.phone,
                provider: session.user.app_metadata?.provider,
                superuser: isSuperUser,
              });
            }

            // Update the stored distinct ID to be the user ID
            useAuthStore.getState().setDistinctId(userId);
            setDistinctId(userId);
          }
        }
      },
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [distinctId]);

  async function signInWithGoogle() {
    console.log("signInWithGoogle start");
    try {
      if (Capacitor.isNativePlatform()) {
        console.log("Signing in with Google on mobile");

        const result = await SocialLogin.login({
          provider: "google",
          options: {
            forcePrompt: true, // Force account selection prompt on iOS
          },
        });

        if (result.provider === "google" && result.result) {
          console.log("Google user:", result.result);

          // For mobile, we get the access token and id token directly
          const googleResult = result.result as any;
          const idToken = googleResult.idToken;
          const accessToken = googleResult.accessToken?.token;

          if (idToken) {
            const nonce = "test";

            const response = await supabase.auth.signInWithIdToken({
              provider: "google",
              token: idToken,
              access_token: accessToken,
              nonce,
            });

            const { data, error } = response;

            if (error) {
              console.error("Error signing in with Supabase:", error);
              return false;
            } else {
              console.log("Signed in user:", data.user);
              await useAuthStore.getState().setUser(data);
              await initPushNotifications(data.user?.id);
              Mixpanel.trackEvent("sign_in_success", {
                name: "User signed in successfully",
                sign_in_method: "google",
              });
              return true;
            }
          }
        }
      } else {
        console.log("Signing in with Google on web");
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: "google",
        });
        console.log("After auth call -->", data, "or error --> ", error);

        if (error) {
          console.error("Error signing in with Supabase:", error);
          return false;
        } else {
          console.log("Supabase OAuth data:", data);
          await useAuthStore.getState().setUser(data);
          Mixpanel.trackEvent("sign_in_success", {
            name: "User signed in successfully",
            sign_in_method: "google",
          });
          return true;
        }
      }
    } catch (error) {
      console.error("Google signin errors 123: ", error);
      return false;
    }

    return false;
  }

  async function signInWithApple() {
    console.log("signInWithApple start");
    try {
      if (Capacitor.isNativePlatform()) {
        console.log("signInWithApple Native start");
        try {
          const options = {
            clientId: "ai.rotobot.app",
            redirectURI: "https://supa.rotobot.ai/auth/v1/callback",
            scopes: "email name",
          };

          const response = await SignInWithApple.authorize(options);
          console.log("apple sign-in response:", response);

          const supabaseResponse = await supabase.auth.signInWithIdToken({
            provider: "apple",
            token: response.response.identityToken,
          });

          const { data, error } = supabaseResponse;

          if (error) {
            console.error("Error signing in with Supabase:", error);
          } else {
            const newUser = {
              givenName: response.response.givenName,
              familyName: response.response.familyName,
              emailApple: response.response.email,
            };

            console.log("newUser --->>>>>:", newUser);
            await useAuthStore.getState().setUser(data);
            await useAuthStore.getState().setAppleProfile(newUser);
            await initPushNotifications(data.user?.id);
            Mixpanel.trackEvent("sign_in_success", {
              name: "User signed in successfully",
              sign_in_method: "apple",
            });
          }
        } catch (error) {
          console.error("Apple sign-in error:", error);
        }
      } else {
        console.log("signInWithApple web");
        try {
          const { data, error } = await supabase.auth.signInWithOAuth({
            provider: "apple",
          });
          if (!error) {
            await useAuthStore.getState().setUser(data);
            Mixpanel.trackEvent("sign_in_success", {
              name: "User signed in successfully",
              sign_in_method: "apple",
            });
            // await initPushNotifications(data)
          }

          console.log(data);
        } catch (error) {
          console.error("Apple sign-in error ", error);
        }
      }
    } catch {
      console.error();
    }
  }

  async function signout() {
    console.log("signout");
    // Get the user ID before signing out
    const currentUserId = useAuthStore.getState().user?.user?.id;

    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("error on during logout ", error);
    } else {
      // Clear user data
      useAuthStore.getState().clearUser();
      setUser(null);
      useAuthStore.getState().clearProfile();

      // Reset the paywall flag on logout
      clearPaywallState();

      // Clear league data
      clearSelectedLeague();
      clearLeagueType();
      clearScoringFormat();
      clearPromoState()
      resetCount();
      // Create a new anonymous ID for this device
      const newAnonId = `anon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      useAuthStore.getState().setDistinctId(newAnonId);
      setDistinctId(newAnonId);

      // Reset Mixpanel for anonymous tracking
      Mixpanel.reset();
      Mixpanel.identifyUser(newAnonId);

      console.log("logout successfully");
    }
  }

  const sendSMS = async (phoneNumber: string) => {
    const { data, error } = await supabase.auth.signInWithOtp({
      phone: phoneNumber,
    });
    if (error) {
      console.log("OTP sent error", error);
      return false;
    } else {
      Mixpanel.trackEvent("enter_phone_number", {
        name: "Entered Phone Number for send OTP",
        phone_number: phoneNumber,
      });
      console.log("OTP sent successfully to:", phoneNumber);
      return true;
    }
  };

  const verifyOTPCode = async (phone: string, token: string) => {
    if (token && phone) {
      const { data, error } = await supabase.auth.verifyOtp({
        phone,
        token,
        type: "sms",
      });

      if (error) {
        return false;
      } else {
        setUser(data.session?.user.user_metadata);
        await useAuthStore.getState().setUser(data.session);
        await initPushNotifications(data.user?.id);
        Mixpanel.trackEvent("enter_verification_code", {
          name: "Entered OTP code for verify",
          phone_number: phone,
        });
        Mixpanel.trackEvent("sign_in_success", {
          name: "User signed in successfully",
          sign_in_method: "phone",
        });
        console.log("Verification OTP successfully");

        return true;
      }
    }
  };

  const resendOtpCode = async (phone: string) => {
    setResendOtpCodeLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: phone,
      });

      if (error) {
        console.log("resendOtpCode error", error);
        return false;
      } else {
        console.log("resendOtpCode success", data);
        return true;
      }
    } catch (err) {
      console.error("resendOtpCode unexpected error", err);
      return false;
    } finally {
      setResendOtpCodeLoading(false);
    }
  };

  const getUserProfile = async (userId: string) => {
    if (!userId) {
      return null;
    }
    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (profileData) {
      await useAuthStore.getState().setProfile(profileData);
      return profileData;
    }
    return null;
  };

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event: any, session: any) => {
        console.log("supabase event: ", event);
        if (session == null) {
          console.log("sign_out");
        } else {
          setUser({ ...session.user });
          console.log("User data", session.user.user_metadata);
        }
      },
    );

    // Cleanup subscription
    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const uploadAvatar = async (file: any, userId: string | undefined) => {
    setUploading(true);
    setError(null); // Clear any previous errors

    try {
      const fileName = `${userId}_${Date.now()}_${file.name}`;
      const { data, error } = await supabase.storage
        .from("avatars")
        .upload(fileName, file);

      if (error) {
        setError("Error uploading file");
        console.error("Error uploading file:", error);
        setUploading(false);
        return null;
      }

      const response = supabase.storage.from("avatars").getPublicUrl(fileName);

      if (!response?.data?.publicUrl) {
        setError("Error getting public URL");
        console.error("Error getting public URL");
        setUploading(false);
        return null;
      }

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .single();

      if (profileError) {
        setError("Error fetching profile");
        console.error("Error fetching profile:", profileError);
        setUploading(false);
        return null;
      }

      if (profileData) {
        // Delete old avatar file if it exists
        if (profileData.file_bucket) {
          const { error: deleteError } = await supabase.storage
            .from("avatars")
            .remove([profileData.file_bucket]);
          console.log(deleteError, "deleteError");
        }

        const { data: updateData, error: updateError } = await supabase
          .from("profiles")
          .update({
            avatar_url: response?.data?.publicUrl,
            file_bucket: fileName,
          })
          .eq("id", profileData.id);
        console.log(updateData, "updateData 541");

        if (updateError) {
          setError("Error updating profile");
          console.error("Error updating profile:", updateError);
          setUploading(false);
          return null;
        }

        // Update the profile in the store
        const updatedProfile = { ...profileData, avatar_url: response?.data?.publicUrl, file_bucket: fileName };
        await useAuthStore.getState().setProfile(updatedProfile);
      } else {
        const { data: updateData, error: updateError } = await supabase
          .from("profiles")
          .insert({
            avatar_url: response?.data?.publicUrl,
            user_id: userId,
            file_bucket: fileName,
          });
        console.log(updateData, "updateData 557");

        if (updateError) {
          setError("Error creating profile");
          console.error("Error creating profile:", updateError);
          setUploading(false);
          return null;
        }

        // Set the new profile in the store
        const newProfile = {
          avatar_url: response?.data?.publicUrl,
          user_id: userId,
          file_bucket: fileName,
        };
        await useAuthStore.getState().setProfile(newProfile);
      }

      setAvatarUrl(response?.data?.publicUrl);

      console.log(response?.data?.publicUrl, "response?.data?.publicUrl 575");
      setUploading(false);
      return response?.data?.publicUrl;
    } catch (unexpectedError) {
      setError("An unexpected error occurred during avatar upload");
      console.error("Unexpected error in uploadAvatar:", unexpectedError);
      setUploading(false);
      return null;
    }
  };

  const getUserData = async () => {
    const useStorage = useAuthStore.getState().user;
    const userResponse = await supabase.auth.getUser(useStorage?.access_token);
    console.log("useStorage?.access_token  ", useStorage?.access_token);
    if (userResponse.error) {
      console.error("Error getting user data" + userResponse.error);
      signout();
      window.location.href = "/";
      return null;
    }
    return userResponse;
  };

  const getProfileForDetails = async (userId: string | undefined) => {
    const tag = "getProfileForDetails";
    if (!userId) {
      console.error(`${tag} - userId is undefined`);
      return null;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select(
        "user_email, user_name, user_dob, referral_code, first_name, last_name, feature_credits, is_super_user",
      )
      .eq("user_id", userId)
      .single();

    if (error) {
      console.error(`${tag} - error:`, error);
      return null;
    }
    return data;
  };

  const getProfileAllowNotifications = async (userId: string | undefined) => {
    const tag = "getProfileAllowNotifications";
    if (!userId) {
      console.error(`${tag} - userId is undefined`);
      return null;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("allow_notifications")
      .eq("user_id", userId)
      .single();

    if (error) {
      console.error(`${tag} - error:`, error);
      return null;
    }
    return data;
  };

  const signupProfile = async (
    userId: string,
    firstName: string,
    lastName: string,
    email: string,
    referralCode: string,
  ) => {
    const userProfile = await getUserProfile(userId);
    Mixpanel.trackEvent("user_details_complete", {
      name: "User Details Complete",
    });
    if (userProfile) {
      const { data, error } = await supabase
        .from("profiles")
        .update({
          user_name: firstName + " " + lastName,
          first_name: firstName,
          last_name: lastName,
          user_email: email,
          referral_code: referralCode,
        })
        .eq("user_id", userId);

      if (error) {
        return {
          sucess: false,
          error: error,
        };
      } else {
        Mixpanel.trackEvent("first_name_updated", {
          name: "First Name Updated",
          first_name: firstName,
        });
        return {
          sucess: true,
          data: data,
        };
      }
    } else {
      const { data, error } = await supabase.from("profiles").insert({
        user_id: userId,
        user_name: firstName + " " + lastName,
        first_name: firstName,
        last_name: lastName,
        user_email: email,
        referral_code: referralCode,
      });

      if (error) {
        return {
          sucess: false,
          error: error,
        };
      } else {
        Mixpanel.trackEvent("enter_first_name", {
          name: "Entered First Name",
          first_name: firstName,
        });
        return {
          sucess: true,
          data: data,
        };
      }
    }
  };

  const setProfileAllowNotifications = async (
    userId: string,
    allow_notifications: boolean,
  ) => {
    const userProfile = await getUserProfile(userId);

    if (userProfile) {
      const { data, error } = await supabase
        .from("profiles")
        .update({
          allow_notifications: allow_notifications,
        })
        .eq("user_id", userId);
      Mixpanel.trackEvent(
        `turn_${allow_notifications ? "on" : "off"}_push_notification`,
        {
          name: `Turn ${allow_notifications ? "on" : "off"} Push Notification`,
          push_notifications_enabled: allow_notifications,
        },
      );
    } else {
      const { data, error } = await supabase.from("profiles").insert({
        user_id: userId,
        allow_notifications: allow_notifications,
      });
      Mixpanel.trackEvent(
        `turn_${allow_notifications ? "on" : "off"}_push_notification`,
        {
          name: `Turn ${allow_notifications ? "on" : "off"} Push Notification`,
          push_notifications_enabled: allow_notifications,
        },
      );
    }
  };

  const getSessionwitAccessToken = async (accessToken: string) => {
    const { data, error } = await supabase.auth.getUser(accessToken);

    if (data) {
      return data;
    }
    return null;
  };

  const saveAppleSubscription = async (userId: string, product: any) => {
    const userProfile = await getUserProfile(userId);

    if (userProfile) {
      const { data, error } = await supabase
        .from("profiles")
        .update({
          apple_subscription: product,
        })
        .eq("user_id", userId);

      if (error) {
        console.error("error creating apple subscription", error);
      }
    } else {
      const { data, error } = await supabase.from("profiles").insert({
        user_id: userId,
        apple_subscription: product,
      });

      if (error) {
        console.error("error saving apple subscription", error);
      }
    }
  };

  async function deactivateAccount(userId: string) {
    console.log("deactivateAccount");
    const { error } = await supabase
      .from("profiles")
      .delete()
      .eq("user_id", userId);
    if (error) {
      console.error("error on during delete profile.", error);
    } else {
      const { error } = await supabase.auth.admin.deleteUser(userId);
      if (error) {
        console.error("error on during delete account.", error);
      } else {
        signout();
      }
    }
  }

  const assignPaywallGroup = async (userId: string) => {
    console.log("assignPaywallGroup called");
    try {
      // First check if user already has a paywall assignment
      const { data: existingProfile, error: fetchError } = await supabase
        .from("profiles")
        .select("hard_paywall")
        .eq("user_id", userId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error("Error checking existing paywall assignment:", fetchError);
        return null;
      }


      // Generate random A/B test assignment
      const hard_paywall = Math.random() < 0.5;

      const { data, error } = await supabase
        .from("profiles")
        .update({ hard_paywall })
        .eq("user_id", userId)
        .select();

      if (error) {
        console.error("Error updating paywall group:", error);
        return null;
      }

      console.log("User assigned to paywall group:", { userId, hard_paywall });
      return data;
    } catch (err) {
      console.error("Failed to update profile:", err);
      return null;
    }
  };



  return (
    <AuthContext.Provider
      value={{
        signInWithGoogle,
        signInWithApple,
        signout,
        sendSMS,
        verifyOTPCode,
        resendOtpCode,
        uploadAvatar,
        useAuthStore,
        user,
        getUserProfile,
        getUserData,
        getProfileForDetails,
        getProfileAllowNotifications,
        signupProfile,
        setProfileAllowNotifications,
        getSessionwitAccessToken,
        saveAppleSubscription,
        deactivateAccount,
        uploading,
        avatarUrl,
        setAvatarUrl,
        resendOtpCodeLoading,
        assignPaywallGroup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useSupabaseAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};
